import React, { useEffect, useState } from "react";
import { Timeline } from "flowbite-react";
import { CurrentEiaFuelRateNotice } from "@src/common/components/Widgets/OrdersPriceWidget/components/CurrentEiaFuelRateNotice";
import { formatTitleCaseWithUnderscores } from "@src/common/lib/textUtils";
import { useSpring, animated } from "react-spring";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import {
  Label,
  OrderPriceTaxBreakdown,
  PackageItem,
  PricingBreakDown,
  PricingList,
  Stop,
  StopCompletionReason,
  useUpdateInternalNoteMutation,
} from "@api/graphql/generated/generated-types";
import CourieButton from "@src/common/components/Button/Button";
import { OrderAdditionalInfoModal } from "./OrderAdditionalInfoModal";
import { PricingBreakDownModal } from "./modals/PricingBreakdownModal";
import InternalNote, { InternalNotePanelProps } from "./InternalNote";
import { InvoiceStatusPanel } from "./InvoiceStatusPanel/InvoiceStatusPanel";
import { LabelBadge } from "@src/common/components/LabelManagement/components/LabelBadge";
import classNames from "classnames";
import { LayersManager } from "baseui/layer";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useActionBy } from "@src/common/hooks/useActionBy";
import { useCourieStore } from "@src/common/lib/store";
import { useAuth } from "@src/common/hooks/useAuth";
import { useCourierLocale } from "@src/common/courierLocale";

export type GeneralPanelProps = {
  title: string;
  value: string;
};

export type StopPanelPropsBodyItem = {
  title: string;
  value: string;
  className?: string;
};

export type PricingPanelProps = {
  title: string | number;
  value: string;
  breakdown: PricingBreakDown | undefined;
  pricingList: PricingList | undefined | null;
  showAnimation?: boolean;
  taxBreakDown?: OrderPriceTaxBreakdown | null;
  /** Subtotal before tax (for breakdown modal). When absent, modal falls back to title. */
  total?: number;
  totalAfterTax?: number;
  /**
   * Show the current EIA fuel rate inside the price box, under the estimated price.
   * Opt in only where "current" is the right rate — i.e. an order being entered/priced
   * now. Leave off for existing orders: their price came from the rate at pricing time,
   * which the breakdown already shows per line, and a "current rate" next to an old
   * price would suggest the two should match when they legitimately may not.
   */
  showCurrentEiaRate?: boolean;
  /** Selected vehicle type for the order being entered; decides gas vs diesel. */
  vehicleTypeId?: string;
};

export type StopPanelProps = {
  title: string;
  subtext?: string;
  bodyItems?: StopPanelPropsBodyItem[];
  stop?: Stop | undefined;
};

export type AdditionalInfoPanelProps = {
  /** Cheap count from the shipments API; the full pairs load on demand from a modal. */
  count: number;
  orderId: string;
};

export type OrderDetailsWidgetDataProps = {
  panelName:
    | "General"
    | "Labels"
    | "Stops"
    | "Pricing"
    | "internalNote"
    | "Package Items"
    | "Package Description"
    | "Additional Information";
  generalPanelProps?: GeneralPanelProps[];
  orderLabels?: Label[];
  stopPanelProps?: StopPanelProps[];
  pricingPanelProps?: PricingPanelProps;
  internalNotePanelProps?: InternalNotePanelProps;
  packageItems?: PackageItem[];
  packageDescription?: string;
  additionalInfoPanelProps?: AdditionalInfoPanelProps;
  hasPricingRoute?: boolean;
};

interface NumberIconProps extends React.SVGProps<SVGSVGElement> {
  index: number;
}

type Props = {
  orderDetails: OrderDetailsWidgetDataProps[];
  refetchCoreData?: () => void;
  orderId?: string;
};

export const NumberIcon: React.FC<NumberIconProps> = ({ index, ...props }) => (
  <svg width="30" height="30" {...props}>
    <text
      x="50%"
      y="50%"
      textAnchor="middle"
      dy=".3em"
      fontSize="16"
      fontWeight="bold"
    >
      {index}
    </text>
  </svg>
);

const OrderDetailsWidget = ({
  orderDetails = [],
  refetchCoreData,
  orderId,
}: Props) => {
  const { formatWeight, currencyDisplay } = useCourierLocale();
  const [animatedValue, setAnimatedValue] = useState<any>(0);
  const [pricingBreakdownModalOpen, setPricingBreakdownModalOpen] =
    useState(false);
  const { number } = useSpring({
    from: { number: 0 },
    to: {
      number: animatedValue,
    },
    reset: false,
    delay: 0,
  });
  const { courierId, hasPermission } = useAuth();
  const { updatedBy } = useActionBy();
  const { showToast } = useCourieStore();
  const [
    updateInternalNoteMutation,
    { loading: updateInternalNoteMutationLoading },
  ] = useUpdateInternalNoteMutation();

  useEffect(() => {
    if (Array.isArray(orderDetails)) {
      let pricingPanelFound = orderDetails.find(
        (panel) => panel.panelName === "Pricing",
      );
      if (
        pricingPanelFound &&
        typeof pricingPanelFound.pricingPanelProps?.title === "number"
      ) {
        if (pricingPanelFound.pricingPanelProps?.showAnimation) {
          const numberTitle = pricingPanelFound.pricingPanelProps?.title;
          setAnimatedValue(numberTitle);
        }
      }
    }
  }, [orderDetails]);

  const hasDimensions = (item: PackageItem): boolean => {
    return (
      item.width !== null ||
      item.height !== null ||
      item.length !== null ||
      item.weight !== null
    );
  };

  const saveInternalNote = async (textToSave?: string, shipmentId?: string) => {
    if (!shipmentId || !updatedBy || !courierId) {
      return false;
    }
    try {
      await updateInternalNoteMutation({
        variables: {
          courierId: courierId,
          shipmentId: shipmentId,
          internalNote: textToSave || "",
          updatedBy: updatedBy,
        },
      });
      showToast({
        message: "Note updated successfully",
        type: "success",
      });
      refetchCoreData && refetchCoreData();
      return true;
    } catch (error: any) {
      showErrorToast(error, showToast);
      return false;
    }
  };

  if (orderDetails.length) {
    return (
      <div
        className={`p-3 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-2xl`}
      >
        {orderDetails.map((panel: OrderDetailsWidgetDataProps, i) => {
          if (panel.panelName === "Labels") {
            const orderLabels: Label[] = panel.orderLabels || [];
            return (
              // Extra room above: the note's input sits directly overhead, and
              // without it the Labels heading reads as part of that field.
              <div key={i} className="mt-4 mb-3">
                <span className="font-semibold">Labels</span>
                {/* An explicit empty state rather than hiding the section: a
                    dispatcher checking whether a bulk relabel landed needs to
                    tell "this order has none" apart from "this never loaded". */}
                {orderLabels.length === 0 ? (
                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    No labels
                  </div>
                ) : (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {orderLabels.map((label: Label) => (
                      <LabelBadge key={label.id} label={label} size="sm" />
                    ))}
                  </div>
                )}
              </div>
            );
          }

          if (panel.panelName === "internalNote") {
            return (
              <div key={i}>
                <span className="font-semibold">Note</span>
                <InternalNote
                  key={i}
                  {...panel.internalNotePanelProps}
                  saveAction={async (textToSave?: string) => {
                    return await saveInternalNote(
                      textToSave,
                      panel.internalNotePanelProps?.shipmentId,
                    );
                  }}
                  loading={updateInternalNoteMutationLoading}
                />
              </div>
            );
          }

          if (panel.panelName === "Stops") {
            return (
              <div
                key={i}
                className="flex flex-col gap-2 bg-white dark:bg-gray-700 rounded-md p-3 pt-2 text-gray-600 dark:text-gray-300"
              >
                <div className="flex items-center justify-between -ml-3">
                  <span className="font-semibold">
                    Stops ({panel.stopPanelProps?.length})
                  </span>
                </div>
                {panel.hasPricingRoute && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 mb-2 -ml-3">
                    Stops are shown in the order defined by their pricing route.
                  </span>
                )}
                {panel.stopPanelProps?.map(
                  (item: StopPanelProps, i: number) => {
                    const completed = item.stop?.completedAt ? true : false;
                    const isAttempted =
                      item.stop?.completionReason ===
                      StopCompletionReason.Attempted;
                    return (
                      <Timeline key={i}>
                        <Timeline.Item>
                          <Timeline.Point
                            icon={() => (
                              <div style={{ height: 30, width: 30 }}>
                                <NumberIcon
                                  index={i + 1}
                                  className={classNames("rounded-full", {
                                    "border rounded-full": true,
                                    "bg-green-300 dark:bg-green-600": completed,
                                    "bg-orange-300 dark:bg-orange-600":
                                      isAttempted,
                                    "bg-white dark:bg-gray-700": !completed,
                                  })}
                                />
                              </div>
                            )}
                          />
                          <Timeline.Content>
                            <Timeline.Title className="font-semibold text-base">
                              {formatTitleCaseWithUnderscores(item.title)}
                            </Timeline.Title>
                            <Timeline.Time>{item.subtext}</Timeline.Time>
                            <Timeline.Body>
                              <StopItemBody {...item} />
                              {item.stop?.assignedLabels && item.stop.assignedLabels.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {item.stop.assignedLabels.map((label) => (
                                    <LabelBadge key={label.id} label={label} size="xs" />
                                  ))}
                                </div>
                              )}
                            </Timeline.Body>
                          </Timeline.Content>
                        </Timeline.Item>
                      </Timeline>
                    );
                  },
                )}
              </div>
            );
          }
          if (panel.panelName === "Pricing") {
            if (!hasPermission("orderPricing.view")) return null;
            return (
              <div key={i}>
                <span className="font-semibold">Price</span>
                <div className="grid grid-cols-2 items-center justify-between bg-slate-100 dark:bg-gray-700 rounded-md p-3 text-gray-600 dark:text-gray-300">
                  <div className="flex flex-col gap-2 col-span-1">
                    <span className="text-xl font-bold">
                      {typeof panel.pricingPanelProps?.title === "number" ? (
                        <span className="text-xl font-bold">
                          {panel.pricingPanelProps.showAnimation ? (
                            <animated.span className="text-xl font-bold">
                              {number.to((n: any) => `${currencyDisplay} ${n.toFixed(2)}`)}
                            </animated.span>
                          ) : (
                            <span>{panel.pricingPanelProps?.title}</span>
                          )}
                        </span>
                      ) : (
                        panel.pricingPanelProps?.title
                      )}
                    </span>
                    <div key={i} className="text-sm">
                      <span className="font-normal">
                        {panel.pricingPanelProps?.value}
                      </span>
                    </div>
                    {panel.pricingPanelProps?.showCurrentEiaRate && (
                      <div className="mt-1">
                        <CurrentEiaFuelRateNotice
                          vehicleTypeId={panel.pricingPanelProps?.vehicleTypeId}
                        />
                      </div>
                    )}
                    {panel.pricingPanelProps?.taxBreakDown?.taxItems &&
                      panel.pricingPanelProps.taxBreakDown.taxItems.length >
                        0 && (
                        <div className="mt-2 text-sm text-slate-400 dark:text-gray-400">
                          {panel.pricingPanelProps.total != null && (
                            <div className="flex justify-between py-1">
                              <span>Total Before Tax</span>
                              <span>
                                {currencyDisplay}{panel.pricingPanelProps.total}
                              </span>
                            </div>
                          )}
                          {panel.pricingPanelProps.taxBreakDown.taxItems.map(
                            (item, index) => (
                              <div
                                key={`tax-${index}`}
                                className="flex justify-between py-1"
                              >
                                <span>{item.name}</span>
                                <span>{currencyDisplay}{item.amount}</span>
                              </div>
                            ),
                          )}
                          <div className="flex justify-between py-1 text-slate-500 dark:text-gray-300 font-semibold">
                            <span>Total After Tax</span>
                            <span>
                              {currencyDisplay}{panel.pricingPanelProps.totalAfterTax}
                            </span>
                          </div>
                        </div>
                      )}
                  </div>
                  {panel.pricingPanelProps?.breakdown && (
                    <a
                      className="col-span-1 flex justify-end"
                      onClick={() => setPricingBreakdownModalOpen(true)}
                    >
                      <QuestionMarkCircleIcon className="h-6 w-6 mr-2 hover:text-slate-900 dark:hover:text-gray-200 text-slate-500 dark:text-gray-400 cursor-pointer" />
                    </a>
                  )}
                  {panel.pricingPanelProps?.breakdown && (
                    <LayersManager zIndex={20}>
                      <PricingBreakDownModal
                        isOpen={pricingBreakdownModalOpen}
                        onClose={() => setPricingBreakdownModalOpen(false)}
                        orderId={orderId}
                        breakdown={panel.pricingPanelProps?.breakdown}
                        total={
                          panel.pricingPanelProps?.total != null
                            ? panel.pricingPanelProps.total.toString()
                            : panel.pricingPanelProps?.title.toString()
                        }
                        pricingList={panel.pricingPanelProps?.pricingList}
                        taxBreakDown={panel.pricingPanelProps?.taxBreakDown}
                        totalAfterTax={panel.pricingPanelProps?.totalAfterTax?.toString()}
                      />
                    </LayersManager>
                  )}
                </div>
              </div>
            );
          }
          if (panel.panelName === "Package Description") {
            return (
              <div key={"Package Description"}>
                <span className="font-semibold">Package Description</span>
                <div className="flex flex-col gap-2 rounded-md border dark:border-gray-700 p-2 text-gray-600 dark:text-gray-300">
                  <div className="text-sm">{panel.packageDescription}</div>
                </div>
              </div>
            );
          }
          if (panel.panelName === "Package Items") {
            if (!panel.packageItems || panel.packageItems.length === 0) {
              return null;
            }
            const itemLabel =
              panel.packageItems.length === 1 ? "Package" : "Packages";

            return (
              <div key={i}>
                <span className="font-semibold">
                  {itemLabel} ({panel.packageItems.length})
                </span>
                <div className="grid grid-cols-2 border dark:border-gray-700 rounded-md ">
                  {panel.packageItems.map((item, index) => (
                    <div key={index} className="p-2 w-full">
                      <div className="h-full flex flex-col items-center text-center bg-white dark:bg-gray-700 rounded-lg overflow-hidden border dark:border-gray-600 shadow-md">
                        <div className="w-full py-2">
                          <div className="font-bold mb-1">
                            Package Item {index + 1}
                          </div>
                          {hasDimensions(item) ? (
                            <>
                              {item.width && (
                                <p className="text-gray-700 dark:text-gray-300 mb-1">
                                  Width: {item.width} in
                                </p>
                              )}
                              {item.height && (
                                <p className="text-gray-700 dark:text-gray-300 mb-1">
                                  Height: {item.height} in
                                </p>
                              )}
                              {item.length && (
                                <p className="text-gray-700 dark:text-gray-300 mb-1">
                                  Length: {item.length} in
                                </p>
                              )}
                              {item.weight && (
                                <p className="text-gray-700 dark:text-gray-300">
                                  Weight: {formatWeight(item.weight)}
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="text-gray-700 dark:text-gray-300 px-3 text-sm">
                              Details unavailable
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          if (panel.panelName === "General") {
            return (
              <div key={i} className=" my-4">
                <span className="font-semibold">Info</span>
                <div className="flex flex-col gap-2 rounded-md border dark:border-gray-700 p-1 py-3 text-gray-600 dark:text-gray-300">
                  {panel.generalPanelProps?.map(
                    (item: GeneralPanelProps, i: number) => (
                      <div
                        key={i}
                        className="text-sm flex space-between w-full pl-4 pr-4"
                      >
                        <span className="flex-1 text-slate-400 dark:text-gray-400">
                          {item.title}{" "}
                        </span>
                        <span className="flex-1 font-bold  ">{item.value}</span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            );
          }
          if (panel.panelName === "Additional Information") {
            const additionalInfo = panel.additionalInfoPanelProps;
            if (!additionalInfo || additionalInfo.count === 0 || !courierId) {
              return null;
            }
            return (
              <AdditionalInfoSection
                key={i}
                count={additionalInfo.count}
                orderId={additionalInfo.orderId}
                courierId={courierId}
              />
            );
          }
        })}
        {orderId && <InvoiceStatusPanel orderId={orderId} />}
      </div>
    );
  }
  return null;
};

function StopItemBody({ bodyItems }: StopPanelProps) {
  return (
    <div className="flex flex-col gap-2 mt-1">
      {bodyItems?.map((item: StopPanelPropsBodyItem, i: number) => (
        <div key={i} className={`flex-1 flex flex-col ${item.className}`}>
          <div className="flex-1 text-xs text-slate-400 dark:text-gray-400">
            {item.title}
          </div>
          <div className="flex-1 text-xs font-bold">{item.value}</div>
        </div>
      ))}
    </div>
  );
}

function AdditionalInfoSection({
  count,
  orderId,
  courierId,
}: AdditionalInfoPanelProps & { courierId: string }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  return (
    <div className="my-4">
      <span className="font-semibold dark:text-gray-100">
        Additional Information
      </span>
      <div className="flex flex-col gap-2 rounded-md border dark:border-gray-700 p-3 text-gray-600 dark:text-gray-300">
        <CourieButton
          color="secondary"
          size="sm"
          className="w-fit"
          onClick={() => setIsModalOpen(true)}
        >
          View {count} {count === 1 ? "field" : "fields"}
        </CourieButton>
      </div>
      <OrderAdditionalInfoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        courierId={courierId}
        orderId={orderId}
      />
    </div>
  );
}

export default OrderDetailsWidget;
